import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import MainLayout from "@/components/navigation-drawer.vue";
import MyAccount from "@/components/my-account.vue";

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err);
};

const originalReplace = Router.prototype.replace;
Router.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err);
};

import Login from "@/components/login.vue";
import RenewSubscription from "@/components/renew-subscription.vue";
import Signup from "@/components/signup.vue";
import VerifyOtp from "@/components/verify-otp.vue";
import AcceptInvite from "@/components/accept-invite.vue";
import TeamMembers from "@/components/team-members.vue";
import Terms from "@/components/legal/terms.vue";
import Privacy from "@/components/legal/privacy.vue";
import Dashboard from "@/components/dashboard.vue";
import Users from "@/components/users.vue";
import CreateUser from "@/components/create-user.vue";
import EditUser from "@/components/edir-user.vue";
import ViewUser from "@/components/view-user.vue";
import Transactions from "@/components/transactions.vue";
import CreateTransactions from "@/components/create-transaction.vue";
import EditTransactions from "@/components/edit-transactions.vue";
import Reports from "@/components/reports.vue";
import GstReports from "@/components/gst-reports.vue";
import Reconciliation from "@/components/reconciliation.vue";
import BankReconciliation from "@/components/bank-reconciliation.vue";
import FixedAssets from "@/components/fixed-assets.vue";
import Payroll from "@/components/payroll.vue";
import ChartOfAccounts from "@/components/chart-of-accounts.vue";
import TrialBalance from "@/components/trial-balance.vue";
import BalanceSheet from "@/components/balance-sheet.vue";
import OpeningBalances from "@/components/opening-balances.vue";
import GeneralLedger from "@/components/general-ledger.vue";
import AccrualPnl from "@/components/accrual-pnl.vue";
import Invoices from "@/components/invoices.vue";
import CreateInvoice from "@/components/create-invoice.vue";
import EditInvoice from "@/components/edit-invoice.vue";
import BusinessProfile from "@/components/business-profile.vue";
import Customers from "@/components/customers.vue";
import CreateCustomer from "@/components/create-customer.vue";
import EditCustomer from "@/components/edit-customer.vue";
import Vendors from "@/components/vendors.vue";
import CreateVendor from "@/components/create-vendor.vue";
import EditVendor from "@/components/edit-vendor.vue";
import Purchases from "@/components/purchases.vue";
import CreatePurchase from "@/components/create-purchase.vue";
import EditPurchase from "@/components/edit-purchase.vue";
import ViewPurchase from "@/components/view-purchase.vue";
import CreditNotes from "@/components/credit-notes.vue";
import CreateCreditNote from "@/components/create-credit-note.vue";
import EditCreditNote from "@/components/edit-credit-note.vue";
import ViewCreditNote from "@/components/view-credit-note.vue";
import DebitNotes from "@/components/debit-notes.vue";
import CreateDebitNote from "@/components/create-debit-note.vue";
import EditDebitNote from "@/components/edit-debit-note.vue";
import ViewDebitNote from "@/components/view-debit-note.vue";
import Quotations from "@/components/quotations.vue";
import CreateQuotation from "@/components/create-quotation.vue";
import EditQuotation from "@/components/edit-quotation.vue";
import ViewQuotation from "@/components/view-quotation.vue";
import ViewInvoice from "@/components/view-invoice.vue";
import ViewTransaction from "@/components/view-transaction.vue";
import CustomerInvoices from "@/components/customer-invoices.vue";
import TermsAndConditions from "@/components/terms-and-conditions.vue";
import BankAccount from "@/components/bank-account.vue";
import Products from "@/components/products.vue";
import CreateProduct from "@/components/create-product.vue";
import EditProduct from "@/components/edit-product.vue";
import ViewProduct from "@/components/view-product.vue";
import Support from "@/components/support.vue";
import Tickets from "@/components/tickets.vue";
import EditTicket from "@/components/edit-ticket.vue";
import Help from "@/components/help.vue";
import RenewalPayments from "@/components/renewal-payments.vue";
import ViewRenewalPayment from "@/components/view-renewal-payment.vue";

Vue.use(Router);

const router = new Router({
  mode: "history",
  // Without this, Vue Router preserves whatever scroll position the
  // previous page had (e.g. residual offset left behind on mobile when
  // a focused input blurs during navigation) and carries it into the
  // next route — reset to top on every navigation instead.
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // Guest
    {
      path: "/login",
      component: Login,
      meta: { guestOnly: true },
    },
    {
      path: "/signup",
      component: Signup,
      meta: { guestOnly: true },
    },
    {
      path: "/verify-otp",
      component: VerifyOtp,
      meta: { guestOnly: true },
    },
    {
      path: "/renew",
      component: RenewSubscription,
      // Deliberately no guestOnly/requiresAuth meta — must be reachable both
      // by a locked-out (logged-out) user via a token link, and by an
      // already-authenticated user renewing proactively before expiry.
    },
    {
      path: "/team/accept-invite/:token",
      component: AcceptInvite,
      meta: { guestOnly: true },
    },
    {
      path: "/terms",
      component: Terms,
      // No guestOnly/requiresAuth meta — legal pages must be reachable by
      // logged-out prospects (linked from signup) and logged-in users alike.
    },
    {
      path: "/privacy",
      component: Privacy,
    },

    // Authenticated layout
    {
      path: "/",
      component: MainLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          redirect: "dashboard",
        },
        {
          path: "dashboard",
          component: Dashboard,
        },
        {
          path: "customers",
          component: Customers,
        },
        {
          path: "customers/create",
          component: CreateCustomer,
        },
        {
          path: "customers/:id/edit",
          component: EditCustomer,
        },
        {
          path: "vendors",
          component: Vendors,
        },
        {
          path: "vendors/create",
          component: CreateVendor,
        },
        {
          path: "vendors/:id/edit",
          component: EditVendor,
        },
        {
          path: "purchases",
          component: Purchases,
        },
        {
          path: "purchases/create",
          component: CreatePurchase,
        },
        {
          path: "purchases/:id/edit",
          component: EditPurchase,
        },
        {
          path: "purchases/:id/view",
          component: ViewPurchase,
        },
        {
          path: "debit-notes",
          component: DebitNotes,
        },
        {
          path: "debit-notes/create",
          component: CreateDebitNote,
        },
        {
          path: "debit-notes/:id/edit",
          component: EditDebitNote,
        },
        {
          path: "debit-notes/:id/view",
          component: ViewDebitNote,
        },
        {
          path: "quotations",
          component: Quotations,
        },
        {
          path: "quotations/create",
          component: CreateQuotation,
        },
        {
          path: "quotations/:id/edit",
          component: EditQuotation,
        },
        {
          path: "quotations/:id/view",
          component: ViewQuotation,
        },
        {
          path: "invoices",
          component: Invoices,
        },
        {
          path: "invoices/:id/view",
          component: ViewInvoice,
        },
        {
          path: "credit-notes",
          component: CreditNotes,
        },
        {
          path: "credit-notes/create",
          component: CreateCreditNote,
        },
        {
          path: "credit-notes/:id/edit",
          component: EditCreditNote,
        },
        {
          path: "credit-notes/:id/view",
          component: ViewCreditNote,
        },
        {
          path: "transactions/:id/view",
          component: ViewTransaction,
        },
        {
          path: "customers/:id/invoices",
          component: CustomerInvoices,
        },
        {
          path: "invoices/create",
          component: CreateInvoice,
        },
        {
          path: "invoices/:id/edit",
          component: EditInvoice,
        },
        {
          path: "transactions",
          component: Transactions,
        },
        {
          path: "transactions/create",
          component: CreateTransactions,
        },
        {
          path: "transactions/:id/edit",
          component: EditTransactions,
        },
        {
          path: "users",
          component: Users,
          meta: { requiresAdmin: true },
        },
        {
          path: "users/create",
          component: CreateUser,
          meta: { requiresAdmin: true },
        },
        {
          path: "users/:id/edit",
          component: EditUser,
          meta: { requiresAdmin: true },
        },
        {
          path: "users/:id/view",
          component: ViewUser,
          meta: { requiresAdmin: true },
        },
        {
          path: "renewal-payments",
          component: RenewalPayments,
          meta: { requiresAdmin: true },
        },
        {
          path: "renewal-payments/:id/view",
          component: ViewRenewalPayment,
          meta: { requiresAdmin: true },
        },
        {
          path: "reports",
          component: Reports,
        },
        {
          path: "gst-reports",
          component: GstReports,
        },
        {
          path: "reconciliation",
          component: Reconciliation,
        },
        {
          path: "bank-reconciliation",
          component: BankReconciliation,
        },
        {
          path: "fixed-assets",
          component: FixedAssets,
        },
        {
          path: "payroll",
          component: Payroll,
        },
        {
          path: "chart-of-accounts",
          component: ChartOfAccounts,
        },
        {
          path: "trial-balance",
          component: TrialBalance,
        },
        {
          path: "balance-sheet",
          component: BalanceSheet,
        },
        {
          path: "opening-balances",
          component: OpeningBalances,
        },
        {
          path: "general-ledger",
          component: GeneralLedger,
        },
        {
          path: "accrual-pnl",
          component: AccrualPnl,
        },
        {
          path: "my-account",
          component: MyAccount,
        },
        {
          path: "business-profile",
          component: BusinessProfile,
        },
        {
          path: "terms-and-conditions",
          component: TermsAndConditions,
        },
        {
          path: "bank-account",
          component: BankAccount,
        },
        {
          path: "team-members",
          component: TeamMembers,
        },
        {
          path: "products",
          component: Products,
        },
        {
          path: "products/create",
          component: CreateProduct,
        },
        {
          path: "products/:id/edit",
          component: EditProduct,
        },
        {
          path: "products/:id/view",
          component: ViewProduct,
        },
        {
          path: "support",
          component: Support,
        },
        {
          path: "tickets",
          component: Tickets,
          meta: { requiresAdmin: true },
        },
        {
          path: "tickets/:id/edit",
          component: EditTicket,
          meta: { requiresAdmin: true },
        },
        {
          path: "help",
          component: Help,
        },
      ],
    },

    // Fallback
    {
      path: "*",
      redirect: "/login",
    },
  ],
});

router.beforeEach((to, from, next) => {
  const isAuth = store.getters['auth/isAuthenticated'];
  const user = store.getters['auth/user']; // assuming your user info is stored in Vuex

  const requiresAuth = to.matched.some(route => route.meta.requiresAuth);
  const guestOnly = to.matched.some(route => route.meta.guestOnly);

  // Check if route requires admin
  const requiresAdmin = to.matched.some(route => route.meta.requiresAdmin);

  if (requiresAuth && !isAuth) {
    // Not logged in
    next({ path: "/login", replace: true });
  } else if (guestOnly && isAuth) {
    // Logged in users should not access guest pages
    next({ path: "/dashboard", replace: true });
  } else if (requiresAdmin && (!user || user.userType !== "admin")) {
    // Block non-admins from admin routes
    next({ path: "/dashboard", replace: true });
  } else {
    next();
  }
});

export default router;
